import type { Projection } from '@onflow/shared';
import { Link } from 'react-router-dom';

export interface ProjectionLineProps {
  projection: Projection;
}

/**
 * Section 2 of /review V2 (spec: docs/features/review-page-v2.md §5.2).
 * Renders one deterministic sentence under the verdict, based on the
 * discriminated union returned by the API's projection calc. No formatting
 * logic lives in the calc — the kind drives the copy here.
 *
 * Goal-lock invariant: the user's stated `targetDate` is the denominator
 * for both rendered date and "N days behind/early" wording.
 */
export function ProjectionLine({ projection }: ProjectionLineProps) {
  if (projection.kind === 'on-track') {
    if (projection.daysAhead === 0) {
      return (
        <p className="review-projection">
          On track to hit {Math.round(projection.projectedWeightKg)} kg by{' '}
          {formatTargetDate(projection.targetDate)}.
        </p>
      );
    }
    return (
      <p className="review-projection">
        On track to hit your target {projection.daysAhead} {pluralDay(projection.daysAhead)} early.
      </p>
    );
  }

  if (projection.kind === 'behind') {
    return (
      <p className="review-projection">
        {projection.daysBehind} {pluralDay(projection.daysBehind)} behind your{' '}
        {formatTargetDate(projection.targetDate)} target.
      </p>
    );
  }

  if (projection.kind === 'wrong-direction') {
    return (
      <p className="review-projection">Currently moving away from target. Adjustments needed.</p>
    );
  }

  if (projection.kind === 'flat') {
    return (
      <p className="review-projection">Weight flat. Projection unavailable. Adjustments needed.</p>
    );
  }

  if (projection.kind === 'insufficient-data') {
    return (
      <p className="review-projection review-projection--placeholder">
        Log another weight to see projection.
      </p>
    );
  }

  if (projection.kind === 'goal-passed') {
    return (
      <p className="review-projection review-projection--placeholder">
        Goal date passed. <Link to="/profile">Set a new target</Link>.
      </p>
    );
  }

  // no-goal
  return (
    <p className="review-projection review-projection--placeholder">
      <Link to="/profile">Set a target weight and date</Link> to see projection.
    </p>
  );
}

function formatTargetDate(iso: string): string {
  const dt = new Date(iso);
  const sameYear = dt.getFullYear() === new Date().getFullYear();
  return dt.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

function pluralDay(n: number): string {
  return n === 1 ? 'day' : 'days';
}
